import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Phone, PhoneOff, Video, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 2500;
const RING_ROLES = ['doctor', 'pharmacist', 'patient'];
// Never ring while the user is already inside a call room.
const MUTED_PATHS = ['/doctor/call/', '/psychology/join-call/', '/call/'];

// Classic telephone ringtone (dual tone 440 + 480 Hz, "ring ring" cadence),
// generated with WebAudio so no audio asset is needed. Loops until stopped
// and keeps the phone vibrating alongside it.
const RING_CYCLE_MS = 4000;

const useRingtone = (active) => {
  const ctxRef = useRef(null);
  const timerRef = useRef(null);
  const vibrateRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    let stopped = false;

    const ctxFor = () => {
      if (!ctxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        ctxRef.current = new AC();
      }
      if (ctxRef.current.state === 'suspended') ctxRef.current.resume();
      return ctxRef.current;
    };

    // One burst = both tones together for `dur` seconds.
    const burst = (ctx, at, dur) => {
      [440, 480].forEach((freq) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, at);
        gain.gain.exponentialRampToValueAtTime(0.5, at + 0.04);
        gain.gain.setValueAtTime(0.5, at + dur - 0.05);
        gain.gain.exponentialRampToValueAtTime(0.0001, at + dur);
        osc.connect(gain).connect(ctx.destination);
        osc.start(at);
        osc.stop(at + dur + 0.02);
      });
    };

    const ring = () => {
      try {
        const ctx = ctxFor();
        if (!ctx) return;
        const now = ctx.currentTime;
        burst(ctx, now, 0.9);         // ring...
        burst(ctx, now + 1.1, 0.9);   // ...ring
      } catch { /* audio blocked — the visual ring still shows */ }
      try { navigator.vibrate?.([600, 300, 600, 900]); } catch { /* noop */ }
    };

    // Browsers block audio until the user has interacted once — resume on the
    // first gesture so the very next ring is audible.
    const unlock = () => { try { ctxFor(); ring(); } catch { /* noop */ } };
    window.addEventListener('pointerdown', unlock, { once: true });

    ring();
    timerRef.current = setInterval(() => { if (!stopped) ring(); }, RING_CYCLE_MS);
    return () => {
      stopped = true;
      clearInterval(timerRef.current);
      clearInterval(vibrateRef.current);
      window.removeEventListener('pointerdown', unlock);
      try { navigator.vibrate?.(0); } catch { /* noop */ }
      try { ctxRef.current?.close(); ctxRef.current = null; } catch { /* noop */ }
    };
  }, [active]);
};

/**
 * App-wide full-screen incoming call ring. Doctors/pharmacists get rung when
 * a patient starts a call; patients get rung when a psychology doctor starts
 * a session. Psychology stays anonymous — generic labels only.
 */
export const IncomingCallOverlay = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [ring, setRing] = useState(null);
  const dismissedRef = useRef(new Set());

  const muted = MUTED_PATHS.some((p) => location.pathname.startsWith(p));
  const eligible = !!user && RING_ROLES.includes(user.role) && !muted;

  useEffect(() => {
    if (!eligible) { setRing(null); return undefined; }
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/calls/incoming`, { withCredentials: true });
        if (cancelled) return;
        if (r.data?.ringing && !dismissedRef.current.has(r.data.call_id)) {
          setRing(r.data);
        } else {
          setRing(null);
        }
      } catch { /* stay silent on transient errors */ }
    };
    poll();
    const id = setInterval(poll, POLL_MS);
    // A push arriving while the app is open should ring instantly, not on the
    // next poll tick.
    const onSwMessage = (e) => {
      if (e.data && e.data.type === 'tababa:push:incoming-call') poll();
    };
    navigator.serviceWorker?.addEventListener?.('message', onSwMessage);
    return () => {
      cancelled = true;
      clearInterval(id);
      navigator.serviceWorker?.removeEventListener?.('message', onSwMessage);
    };
  }, [eligible, location.pathname]);

  useRingtone(!!ring);

  if (!ring) return null;

  const isVideo = ring.call_type === 'video';

  const answer = async () => {
    dismissedRef.current.add(ring.call_id);
    const callId = ring.call_id;
    const link = ring.link;
    setRing(null);
    // Silence the repeat push ringing on every device before we navigate.
    try {
      await axios.post(`${API}/calls/${callId}/ring-stop`, {}, { withCredentials: true });
    } catch { /* best effort — the room join stops the ring too */ }
    navigate(link);
  };

  const decline = async () => {
    dismissedRef.current.add(ring.call_id);
    setRing(null);
    try {
      await axios.post(`${API}/calls/${ring.call_id}/decline`, {}, { withCredentials: true });
    } catch { /* best effort */ }
  };

  return (
    <div
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-between bg-[#07203f] text-white px-6 py-12 ${language === 'ar' ? 'rtl' : 'ltr'}`}
      data-testid="incoming-call-overlay"
    >
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 50% 20%, #1e88e5 0%, transparent 55%)',
      }} />

      <div className="relative text-center mt-10">
        <p className="text-sm tracking-widest uppercase text-blue-200/80 mb-6">
          {isVideo
            ? t('مكالمة فيديو واردة', 'Incoming video call', 'Appel vidéo entrant')
            : t('مكالمة صوتية واردة', 'Incoming voice call', 'Appel vocal entrant')}
        </p>
        <div className="relative inline-flex w-32 h-32 mb-6">
          <span className="absolute inset-0 rounded-full bg-white/20 animate-ping" />
          <span className="absolute -inset-4 rounded-full bg-white/10 animate-pulse" />
          <span className="relative w-32 h-32 rounded-full bg-[#1e88e5] flex items-center justify-center shadow-2xl">
            {isVideo ? <Video size={54} /> : <User size={54} />}
          </span>
        </div>
        <h2 className="text-3xl font-extrabold mb-2" data-testid="incoming-call-name">
          {ring.caller_name || t('مستخدم', 'User', 'Utilisateur')}
        </h2>
        {ring.anonymous ? (
          <p className="text-blue-200 text-sm">
            {t('جلسة نفسية — هوية مخفية', 'Psychology session — identity hidden', 'Séance anonyme')}
          </p>
        ) : ring.symptoms ? (
          <p className="text-blue-100 text-sm max-w-md mx-auto line-clamp-3" data-testid="incoming-call-symptoms">
            {ring.symptoms}
          </p>
        ) : null}
      </div>

      <div className="relative flex items-end gap-16 mb-6">
        <button
          onClick={decline}
          className="flex flex-col items-center gap-3 group"
          data-testid="incoming-call-decline"
        >
          <span className="w-20 h-20 rounded-full bg-red-600 flex items-center justify-center shadow-xl transition-transform group-hover:scale-110 group-active:scale-95">
            <PhoneOff size={30} />
          </span>
          <span className="text-sm font-semibold">{t('رفض', 'Decline', 'Refuser')}</span>
        </button>
        <button
          onClick={answer}
          className="flex flex-col items-center gap-3 group"
          data-testid="incoming-call-answer"
        >
          <span className="w-24 h-24 rounded-full bg-emerald-500 flex items-center justify-center shadow-xl transition-transform group-hover:scale-110 group-active:scale-95">
            <Phone size={34} className="animate-bounce" />
          </span>
          <span className="text-sm font-semibold">{t('إجابة', 'Answer', 'Répondre')}</span>
        </button>
      </div>
    </div>
  );
};

export default IncomingCallOverlay;
